(() => {
  if (window.diPexSideWaveReady) return;
  window.diPexSideWaveReady = true;
  const instances = new Map();
  const wide = window.matchMedia('(min-width: 1101px)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const read = key => { try { return sessionStorage.getItem(key) === '1'; } catch { return false; } };
  const save = key => { try { sessionStorage.setItem(key, '1'); } catch {} };
  let paused = read('di-pex-paused');
  const seen = new Set();

  function init(root) {
    if (instances.has(root)) return;
    const section = root.closest('.di-trackside');
    const copy = section && section.querySelector('.di-trackside__copy');
    const routes = section && section.querySelector('.di-route-strip');
    const link = root.querySelector('a');
    const fallback = root.querySelector('img');
    const canvas = root.querySelector('canvas');
    const configNode = root.querySelector('[data-di-pex-wave-config]');
    if (!section || !copy || !routes || !link || !fallback || !canvas || !configNode || !document.getElementById('notepad')) return;
    let config;
    try { config = JSON.parse(configNode.textContent); } catch { return; }
    const context = canvas.getContext('2d');
    const key = 'di-pex-side-wave-v2-' + root.dataset.diPexPeek;
    let played = read(key) || seen.has(key);
    let visible = false;
    let fits = false;
    let destroyed = false;
    let sheet;
    let loading = false;
    let failed = false;
    let raf = 0;
    let current = '';
    let lastWave = -Infinity;
    const cleanup = [];
    const listen = (node, type, fn) => { node.addEventListener(type, fn); cleanup.push(() => node.removeEventListener(type, fn)); };
    const allowed = () => !destroyed && visible && fits && !document.hidden && !paused && !reduced.matches;
    const draw = progress => {
      if (!sheet || !context || typeof Path2D !== 'function') return;
      const path = new Path2D(config.path);
      const arm = new Path2D(config.arm);
      const body = new Path2D('M 0 0 H 512 V 512 H 0 Z ' + config.arm);
      const paint = () => context.drawImage(sheet, config.width, 0, config.width, config.height, 0, 0, 512, 512);
      context.clearRect(0, 0, 512, 512);
      context.save();
      context.translate(15, 15);
      context.scale(.94, .94);
      context.save();
      context.translate(133, 241);
      context.rotate(Math.sin(progress * Math.PI * 6) * Math.sin(progress * Math.PI) * .34);
      context.translate(-133, -241);
      context.clip(arm);
      paint();
      context.restore();
      context.save();
      context.clip(path);
      context.clip(body, 'evenodd');
      paint();
      context.restore();
      context.restore();
      canvas.hidden = false;
      root.classList.add('has-wave');
    };
    const stop = () => {
      cancelAnimationFrame(raf);
      raf = 0;
      current = '';
      delete root.dataset.wave;
      draw(0);
    };
    const animate = pop => {
      if (!allowed() || !sheet || current) return;
      current = 'wave';
      root.dataset.wave = 'playing';
      let start;
      const delay = pop ? 460 : 0;
      const tick = time => {
        raf = 0;
        if (!allowed()) { stop(); return; }
        if (start === undefined) start = time;
        const elapsed = time - start;
        const progress = Math.max(0, (elapsed - delay) / 1900);
        if (progress >= 1) { stop(); return; }
        draw(progress);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    const reflect = () => {
      root.hidden = !fits || (failed && fallback.complete && fallback.naturalWidth === 0);
      root.classList.toggle('is-still', paused || reduced.matches);
      if (!allowed() || root.hidden) stop();
      if (visible && !root.hidden && (paused || reduced.matches || played)) root.classList.add('is-revealed');
    };
    const reveal = () => {
      reflect();
      if (!visible || root.hidden || document.hidden) return;
      if (!sheet && !failed) { load(); return; }
      const pop = !root.classList.contains('is-revealed');
      root.classList.add('is-revealed');
      if (!played && allowed() && sheet) {
        played = true;
        seen.add(key);
        save(key);
        animate(pop);
      }
    };
    const load = () => {
      if (sheet || loading || failed || destroyed) return;
      if (!context || typeof Path2D !== 'function') { failed = true; reveal(); return; }
      loading = true;
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => {
        if (destroyed) return;
        loading = false;
        if (image.naturalWidth < 1024 || image.naturalHeight < 512) { failed = true; reveal(); return; }
        sheet = image;
        draw(0);
        reveal();
      };
      image.onerror = () => { if (!destroyed) { loading = false; failed = true; reveal(); } };
      image.src = config.src;
    };
    const layout = () => {
      const box = section.getBoundingClientRect();
      const bottom = copy.getBoundingClientRect().bottom;
      const end = routes.getBoundingClientRect().top;
      const size = Math.min(168, Math.floor(end - bottom - 60));
      fits = wide.matches && size >= 112;
      if (fits) {
        root.style.setProperty('--pex-size', size + 'px');
        root.style.top = bottom - box.top + 18 + 'px';
      }
      reflect();
    };
    const notice = event => {
      if (event.type === 'pointerenter' && !fine.matches) return;
      root.classList.add('is-revealed');
      if (!sheet) { load(); return; }
      if (performance.now() - lastWave < 3000 || current) return;
      lastWave = performance.now();
      animate(false);
    };
    listen(link, 'pointerenter', notice);
    listen(link, 'focus', notice);
    listen(link, 'click', () => {
      const target = document.getElementById('notepad');
      if (target) target.focus({ preventScroll: true });
    });
    listen(fallback, 'load', reveal);
    listen(fallback, 'error', reflect);
    listen(wide, 'change', layout);
    listen(reduced, 'change', () => { reflect(); reveal(); });
    listen(document, 'visibilitychange', () => { reflect(); if (!document.hidden) reveal(); });
    listen(window, 'resize', layout);
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(layout);
      observer.observe(section);
      observer.observe(copy);
      cleanup.push(() => observer.disconnect());
    }
    layout();
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        const entry = entries[entries.length - 1];
        visible = entry.isIntersecting && entry.intersectionRatio >= .6;
        if (visible) reveal(); else stop();
      }, { threshold: [.6] });
      observer.observe(root);
      cleanup.push(() => observer.disconnect());
    } else {
      failed = true;
      reflect();
      root.classList.add('is-revealed');
    }
    instances.set(root, { reflect, dispose: () => { destroyed = true; stop(); cleanup.forEach(fn => fn()); instances.delete(root); } });
  }
  const scan = scope => scope.querySelectorAll('[data-di-pex-peek]').forEach(init);
  scan(document);
  document.addEventListener('di:pex-motion', event => { paused = Boolean(event.detail && event.detail.paused); instances.forEach(instance => instance.reflect()); });
  document.addEventListener('shopify:section:load', () => scan(document));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => { if (event.target.contains(root)) instance.dispose(); });
  });
})();
